import {
  useCreate,
  useDataProvider,
  useGetIdentity,
  useNavigation,
  useNotification,
  useUpdate,
} from "@refinedev/core";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { Sentry } from "@/lib/sentry";
import type { IIdentity } from "@/providers/auth-provider";
import type {
  DiscoverTestCreateResponse,
  DiscoverTestRead,
  InstagramSiteCreatePayload,
  RunConfig,
  RunTriggerResponse,
  SiteRead,
  SiteUpdatePayload,
  UnblockSessionRead,
} from "@/types/tina";

export interface TriggerSiteVariables {
  limit?: number | null;
  config?: RunConfig | null;
}

export const useSitesRepository = () => {
  const { custom, getOne } = useDataProvider()("tina");
  const { mutateAsync: createAsync } = useCreate();
  const { mutateAsync: updateAsync } = useUpdate();
  const { open } = useNotification();
  const { show } = useNavigation();
  const { data: identity } = useGetIdentity<IIdentity>();

  async function trigger(
    name: string,
    values: TriggerSiteVariables = {},
  ): Promise<void> {
    Sentry.addBreadcrumb({
      category: "tina-sites.trigger",
      message: "Triggering site run",
      level: "info",
      data: { name },
    });
    try {
      const response = await custom!({
        url: "",
        method: "post",
        // Same attribution as POST /runs: Tina stores it as runs.triggered_by,
        // which "Today's coverage" needs to count a manual run at all.
        payload: { ...values, triggered_by: identity?.id },
        meta: { urlPath: `sites/${encodeURIComponent(name)}/trigger` },
      });
      const result = response.data as RunTriggerResponse;
      open?.({
        type: "success",
        message: "Run triggered",
        description: `Site ${name}`,
      });
      show("tina-runs", result.run_id);
    } catch (error) {
      Sentry.captureException(error);
      open?.({
        type: "error",
        message: "Failed to trigger run",
        description: toHumanErrorMessage(error),
      });
      throw error;
    }
  }

  async function get(name: string): Promise<SiteRead> {
    // The tina data provider's getOne returns the raw body (see tina-data-provider.ts).
    const response = await getOne!({ id: name, resource: "tina-sites" });
    return response as unknown as SiteRead;
  }

  async function update(name: string, payload: SiteUpdatePayload): Promise<void> {
    await updateAsync({
      resource: "tina-sites",
      id: name,
      values: payload,
      successNotification: { type: "success", message: `Updated ${name}` },
      errorNotification: false,
    });
  }

  async function setEnabled(name: string, enabled: boolean): Promise<void> {
    await update(name, { enabled });
  }

  /**
   * Creates an Instagram site. Deliberately silent on both success and failure:
   * the form owns the toast (it names the groups) and the error mapping (409 →
   * an inline `name` error), so it needs the rejection to reach it untouched.
   */
  async function create(
    payload: InstagramSiteCreatePayload,
  ): Promise<SiteRead> {
    const response = await createAsync({
      resource: "tina-sites",
      values: payload,
      successNotification: false,
      errorNotification: false,
    });
    return response.data as unknown as SiteRead;
  }

  /**
   * Instagram sites whose name contains `handle` — powers the soft
   * duplicate-handle warning. `GET /sites` only filters on name (no config
   * filter exists), so this catches the `handle` / `handle-instagram` naming
   * convention but not an arbitrarily named row. Advisory: any failure resolves
   * to [] rather than blocking the create.
   */
  async function findByHandle(handle: string): Promise<SiteRead[]> {
    try {
      const response = await custom!({
        url: "",
        method: "get",
        query: { name: handle, source_kind: "instagram", limit: 25 },
        meta: { urlPath: "sites" },
      });
      const body = response.data as { items?: SiteRead[] };
      return body.items ?? [];
    } catch {
      return [];
    }
  }

  async function startDiscoverTest(
    name: string,
  ): Promise<DiscoverTestCreateResponse> {
    const response = await custom!({
      url: "",
      method: "post",
      meta: { urlPath: `sites/${encodeURIComponent(name)}/discover-test` },
    });
    return response.data as DiscoverTestCreateResponse;
  }

  async function getDiscoverTest(
    name: string,
    id: string,
  ): Promise<DiscoverTestRead> {
    const response = await custom!({
      url: "",
      method: "get",
      meta: {
        urlPath: `sites/${encodeURIComponent(name)}/discover-test/${id}`,
      },
    });
    return response.data as DiscoverTestRead;
  }

  async function startUnblockSession(
    name: string,
  ): Promise<UnblockSessionRead> {
    const response = await custom!({
      url: "",
      method: "post",
      meta: { urlPath: `sites/${encodeURIComponent(name)}/unblock` },
    });
    return response.data as UnblockSessionRead;
  }

  async function saveUnblockSession(
    name: string,
    sessionId: string,
    contextId: string,
  ): Promise<void> {
    await custom!({
      url: "",
      method: "post",
      payload: { context_id: contextId },
      meta: {
        urlPath: `sites/${encodeURIComponent(name)}/unblock/${sessionId}/save`,
      },
    });
  }

  async function closeUnblockSession(
    name: string,
    sessionId: string,
  ): Promise<void> {
    await custom!({
      url: "",
      method: "delete",
      meta: {
        urlPath: `sites/${encodeURIComponent(name)}/unblock/${sessionId}`,
      },
    });
  }

  return {
    trigger,
    get,
    create,
    update,
    setEnabled,
    findByHandle,
    startDiscoverTest,
    getDiscoverTest,
    startUnblockSession,
    saveUnblockSession,
    closeUnblockSession,
  };
};
